import {
  SectionAccordionBodyKeyContext,
  SectionAccordionRowBoundariesContext,
  SectionAccordionViewportContext,
} from "@-/dev.improve.haniverse.components.ui.section-accordion";
import {
  DeckCardListSection,
  DeckCardSectionLoadingFooter,
  type DeckCardTile,
} from "@-/dev.improve.haniverse.routes.nav.app.deck._components.deck-card-section";
import { useDeckSectionCards } from "@-/dev.improve.haniverse.routes.nav.app.deck._lib.use-deck-section-cards";
import type { DeckDisplayMode } from "@-/dev.improve.haniverse.routes.nav.app.deck.entities";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeStagesByGrapheme } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { DeckCardSurfaceFrame } from "#deck-card-surface-frame";

export type DeckCardSurfaceProps = {
  cardCount: number;
  coreSliceByFront: Pick<ReadonlyMap<string, MorphemeDetailsTileSlice>, "get">;
  countLabel?: string;
  displayMode: DeckDisplayMode;
  emptyMessage: string;
  fronts: readonly string[];
  isRefetching?: boolean;
  onToggleDetails?: () => void;
  stageByFront: MorphemeStagesByGrapheme;
  subtitle?: string;
  tilesByFront: Map<string, DeckCardTile[]>;
  title: string;
};

type RowBoundariesChangeHandler = (boundaries: readonly number[], rowPitch: number) => void;

/**
 * Shares Deck's row windowing and detail pagination without rendering any accordion chrome. Must
 * sit inside a scroller that provides `SectionAccordionViewportContext` (`DeckCardSurfaceScrollView`
 * or Upcoming's sheet); `bodyKey` identifies this surface in that viewport and is its test ID.
 */
export function DeckCardSurface(
  props: DeckCardSurfaceProps & {
    bodyKey: string;
    onRowBoundariesChange?: RowBoundariesChangeHandler;
  },
) {
  const {
    bodyKey,
    cardCount,
    coreSliceByFront,
    countLabel,
    displayMode,
    emptyMessage,
    fronts,
    isRefetching,
    onRowBoundariesChange,
    onToggleDetails,
    stageByFront,
    subtitle,
    tilesByFront,
    title,
  } = props;
  const cards = useDeckSectionCards({ coreSliceByFront, fronts });
  const { hasMore, pendingDetailsFronts, revealMore } = cards;
  const viewport = useContext(SectionAccordionViewportContext);
  const isFocused = useIsNavigationFocused();
  const lastRevealedHeight = useRef(-1);
  const [bodyTop, setBodyTop] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const reportBoundaries = useCallback(
    (boundaries: readonly number[], rowPitch: number) =>
      onRowBoundariesChange?.(
        boundaries.map((boundary) => bodyTop + 1 + headerHeight + boundary),
        rowPitch,
      ),
    [bodyTop, headerHeight, onRowBoundariesChange],
  );
  useLayoutEffect(() => {
    if (cards.cards.length === 0) {
      onRowBoundariesChange?.([], 0);
    }
  }, [cards.cards.length, onRowBoundariesChange]);
  useEffect(() => {
    if (viewport === null || !isFocused || !hasMore) {
      return;
    }
    // A page containing only hidden completed cards can settle without growing the surface.
    // Let that settled page advance too, rather than leaving its loading footer waiting forever.
    if (pendingDetailsFronts.size === 0) {
      lastRevealedHeight.current = -1;
    }
    const checkEnd = () => {
      const body = viewport.getBody(bodyKey);
      const { height, offset } = viewport.getViewport();
      if (body === undefined || height <= 0 || body.height === lastRevealedHeight.current) {
        return;
      }
      if (body.y + body.height >= offset && body.y + body.height <= offset + height * 2) {
        lastRevealedHeight.current = body.height;
        revealMore();
      }
    };
    checkEnd();
    return viewport.subscribe(checkEnd);
  }, [bodyKey, hasMore, pendingDetailsFronts, revealMore, isFocused, viewport]);
  return (
    <SectionAccordionBodyKeyContext value={bodyKey}>
      <DeckCardSurfaceFrame
        cardCount={cardCount}
        countLabel={countLabel}
        isRefetching={isRefetching}
        onHeaderLayout={({ nativeEvent: { layout } }) => setHeaderHeight(layout.height)}
        onLayout={({ nativeEvent: { layout } }) => {
          viewport?.setBody(bodyKey, layout);
          setBodyTop(layout.y);
        }}
        onToggleDetails={onToggleDetails}
        showDetails={displayMode === "morpheme-details-tiles"}
        subtitle={subtitle}
        testID={bodyKey}
        title={title}
      >
        <SectionAccordionRowBoundariesContext
          value={onRowBoundariesChange === undefined ? null : reportBoundaries}
        >
          <DeckCardListSection
            cards={cards.cards}
            deckCardTilesByCharacter={tilesByFront}
            displayMode={displayMode}
            emptyMessage={emptyMessage}
            footer={cards.hasMore ? <DeckCardSectionLoadingFooter /> : null}
            isEditing={false}
            pendingDetailsFronts={cards.pendingDetailsFronts}
            stageByGrapheme={stageByFront}
          />
        </SectionAccordionRowBoundariesContext>
      </DeckCardSurfaceFrame>
    </SectionAccordionBodyKeyContext>
  );
}
